// Test Mode App - Bypasses Azure AD for testing and development
import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import useAuthentication from "./hooks/useAuthentication";
import useEmployeeProfile from "./hooks/useEmployeeProfile";
import { AuthProvider } from "./auth/AuthContext";
import { LeaveProvider } from "./context/LeaveContext";
import { EstablishmentProvider } from "./context/EstablishmentContext";
import EmployeePortal from "./components/EmployeePortal";
import DebugPanel from "./components/common/DebugPanel";
import EnvironmentBadge from "./components/common/EnvironmentBadge";

export default function TestModeApp() {
  const {
    token,
    isLoading: tokenLoading,
    error: tokenError,
  } = useAuthentication();
  const {
    employeeNo,
    isLoading: profileLoading,
    displayName,
  } = useEmployeeProfile();

  // Mock logout function for test mode
  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.reload();
  };

  // Log app initialization in development
  React.useEffect(() => {
    if (process.env.NODE_ENV === "development" && token && employeeNo) {
      console.log("App initialized:", employeeNo, displayName);
    }
  }, [token, employeeNo, displayName]);

  // Show loading state while fetching token or employee profile
  if (tokenLoading || profileLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
        <p className="mt-4 text-indigo-600 font-semibold">
          {tokenLoading ? "Fetching token..." : "Loading your profile..."}
        </p>
      </div>
    );
  }

  // Show error if token acquisition failed
  if (tokenError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Token Error
          </h2>
          <p className="text-gray-600 mb-4">{tokenError}</p>
          <p className="text-xs text-gray-600 mb-6">
            Check if backend server is running on port 7071
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-indigo-600 text-white py-2 px-6 rounded-lg hover:bg-indigo-700 font-semibold transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show error if user is not an employee (optional - can skip in test mode)
  if (employeeNo === null) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Employee Profile Not Found
          </h2>
          <p className="text-gray-600 mb-4">
            Could not find your employee profile in the system.
          </p>
          <button
            onClick={handleLogout}
            className="bg-indigo-600 text-white py-2 px-6 rounded-lg hover:bg-indigo-700 font-semibold transition-colors"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  // User is authenticated and is an employee - show the app
  // Wrap in AuthProvider, LeaveProvider, and EstablishmentProvider like the normal app
  return (
    <AuthProvider>
      <LeaveProvider>
        <EstablishmentProvider>
          <Routes>
            {/* Public routes - redirect to portal */}
            <Route path="/" element={<Navigate to="/portal" replace />} />
            <Route
              path="/register"
              element={<Navigate to="/portal" replace />}
            />
            <Route
              path="/pricing"
              element={<Navigate to="/portal" replace />}
            />

            {/* Main employee portal */}
            <Route
              path="/portal/*"
              element={<EmployeePortal onLogout={handleLogout} />}
            />

            {/* Fallback route */}
            <Route path="*" element={<Navigate to="/portal" replace />} />
          </Routes>

          {/* Debug panel and environment badge only shown in development */}
          {process.env.NODE_ENV === "development" && (
            <>
              <DebugPanel />
              <EnvironmentBadge />
            </>
          )}
        </EstablishmentProvider>
      </LeaveProvider>
    </AuthProvider>
  );
}
